import { useEffect, useMemo } from 'react'
import BigNumber from 'bignumber.js'
import { kebabCase, toNumber } from 'lodash'
import { useWallet } from 'use-wallet'
import { Toast, toastTypes } from '@pancakeswap-libs/uikit'
import { useSelector, useDispatch } from 'react-redux'
import useRefresh from 'hooks/useRefresh'
import { QuoteToken, Team } from 'config/constants/types'
import {
  fetchBattlefieldPublicDataAsync,
  fetchFarmsPublicDataAsync,
  fetchPoolsPublicDataAsync,
  fetchPoolsUserDataAsync,
  push as pushToast,
  remove as removeToast,
  clear as clearToast,
} from './actions'
import { State, Farm, Pool, Battlefield, ProfileState, TeamsState, AchievementState } from './types'
import { fetchProfile } from './profile'
import { fetchTeam, fetchTeams } from './teams'
import { fetchAchievements } from './achievements'


const ZERO = new BigNumber(0)

export const useFetchPublicData = () => {
  const dispatch = useDispatch()
  const { slowRefresh } = useRefresh()
  useEffect(() => {
    dispatch(fetchFarmsPublicDataAsync())
    dispatch(fetchBattlefieldPublicDataAsync())
    dispatch(fetchPoolsPublicDataAsync())
  }, [dispatch, slowRefresh])
}

// Battlefield

export const useBattlefield = (): Battlefield[] => {
  const battlefield = useSelector((state: State) => state.battlefield.data)
  return battlefield
}

export const useBattlefieldFromPid = (pid): Battlefield => {
  const battlefield = useSelector((state: State) => state.battlefield.data.find((f) => f.pid === pid))
  return battlefield
}

export const useBattlefieldFromSymbol = (lpSymbol: string): Battlefield => {
  const battlefield = useSelector((state: State) => state.battlefield.data.find((f) => f.lpSymbol === lpSymbol))
  return battlefield
}

export const useBattlefieldUser = (pid) => {
  const battlefield = useBattlefieldFromPid(pid)

  return {
    allowance: battlefield.userData ? new BigNumber(battlefield.userData.allowance) : new BigNumber(0),
    tokenBalance: battlefield.userData ? new BigNumber(battlefield.userData.tokenBalance) : new BigNumber(0),
    stakedBalance: battlefield.userData ? new BigNumber(battlefield.userData.stakedBalance) : new BigNumber(0),
    earnings: battlefield.userData ? new BigNumber(battlefield.userData.earnings) : new BigNumber(0),
    userArmyStrength: battlefield.userData ? new BigNumber(battlefield.userData.userArmyStrength) : new BigNumber(0),
    userArmyPercent: battlefield.userData ? new BigNumber(battlefield.userData.userArmyPercent) : new BigNumber(0),
  }
}

// Farms

export const useFarms = (): Farm[] => {
  const farms = useSelector((state: State) => state.farms.data)
  return farms
}

export const useFarmFromPid = (pid): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.pid === pid))
  return farm
}

export const useFarmFromSymbol = (lpSymbol: string): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.lpSymbol === lpSymbol))
  return farm
}

export const useFarmUser = (pid) => {
  const farm = useFarmFromPid(pid)

  return {
    allowance: farm.userData ? new BigNumber(farm.userData.allowance) : new BigNumber(0),
    tokenBalance: farm.userData ? new BigNumber(farm.userData.tokenBalance) : new BigNumber(0),
    stakedBalance: farm.userData ? new BigNumber(farm.userData.stakedBalance) : new BigNumber(0),
    earnings: farm.userData ? new BigNumber(farm.userData.earnings) : new BigNumber(0),
  }
}

// Pools

export const usePools = (account): Pool[] => {
  const { fastRefresh } = useRefresh()
  const dispatch = useDispatch()
  useEffect(() => {
    if (account) {
      dispatch(fetchPoolsUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])

  const pools = useSelector((state: State) => state.pools.data)
  return pools
}

export const usePoolFromPid = (sousId): Pool => {
  const pool = useSelector((state: State) => state.pools.data.find((p) => p.sousId === sousId))
  return pool
}

// Prices

export const usePriceBnbBusd = (): BigNumber => {
  const pid = 4 // BUSD-BNB LP
  const farm = useFarmFromPid(pid)
  return farm.tokenPriceVsQuote ? new BigNumber(1).div(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceCakeBusd = (): BigNumber => {
  const pid = 10 // KNIGHT-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  return farm.tokenPriceVsQuote ? bnbPriceUSD.times(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceEthBusd = (): BigNumber => {
  const pid = 14 // ETH-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  return ZERO
}

export const usePriceEthBnb = (): BigNumber => {
  const priceBnbBusd = usePriceBnbBusd()
  const priceEthBusd = usePriceEthBusd()
  return priceEthBusd.div(priceBnbBusd)
}

export const usePriceTableBusd = (): BigNumber => {
  const pid = 12 // TABLE-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  return farm.tokenPriceVsQuote ? bnbPriceUSD.times(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceLegendBusd = (): BigNumber => {
  const pid = 5 // LEGEND-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  return farm.tokenPriceVsQuote ? bnbPriceUSD.times(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceSquireBusd = (): BigNumber => {
  const pid = 6 // SQUIRE-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  return farm.tokenPriceVsQuote ? bnbPriceUSD.times(farm.tokenPriceVsQuote) : ZERO
}

export const usePriceShillingBusd = (): BigNumber => {
  const pid = 9 // SHILLING-BNB LP
  const bnbPriceUSD = usePriceBnbBusd()
  const farm = useFarmFromPid(pid)
  if (farm){
    return farm.tokenPriceVsQuote ? bnbPriceUSD.times(farm.tokenPriceVsQuote) : ZERO
  } 
  return ZERO
}

// Toasts
export const useToast = () => {
  const dispatch = useDispatch()
  const helpers = useMemo(() => {
    const push = (toast: Toast) => dispatch(pushToast(toast))

    return {
      toastError: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.DANGER, title, description })
      },
      toastInfo: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.INFO, title, description })
      },
      toastSuccess: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.SUCCESS, title, description })
      },
      toastWarning: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.WARNING, title, description })
      },
      push,
      remove: (id: string) => dispatch(removeToast(id)),
      clear: () => dispatch(clearToast()),
    }
  }, [dispatch])

  return helpers
}

// Profile

export const useFetchProfile = () => {
  const { account } = useWallet()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchProfile(account))
  }, [account, dispatch])
}

export const useProfile = () => {
  const { isInitialized, isLoading, data, hasRegistered }: ProfileState = useSelector((state: State) => state.profile)
  return { profile: data, hasProfile: isInitialized && hasRegistered, isInitialized, isLoading }
}

// Teams

export const useTeam = (id: number) => {
  const team: Team = useSelector((state: State) => state.teams.data[id])
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchTeam(id))
  }, [id, dispatch])

  return team
}

export const useTeams = () => {
  const { isInitialized, isLoading, data }: TeamsState = useSelector((state: State) => state.teams)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchTeams())
  }, [dispatch])

  return { teams: data, isInitialized, isLoading }
}

// Achievements

export const useFetchAchievements = () => {
  const { account } = useWallet()
  const dispatch = useDispatch()

  useEffect(() => {
    if (account) {
      dispatch(fetchAchievements(account))
    }
  }, [account, dispatch])
}

export const useAchievements = () => {
  const achievements: AchievementState['data'] = useSelector((state: State) => state.achievements.data)
  return achievements
}

export const useTotalValue = (): BigNumber => {
  const farms = useFarms()
  const battlefield = useBattlefield()
  const pool = usePoolFromPid(0)
  const bnbPrice = usePriceBnbBusd()
  const ethPrice = usePriceEthBusd()
  const cakePrice = usePriceCakeBusd()
  const legendPrice = usePriceLegendBusd()
  const tablePrice = usePriceTableBusd()
  const squirePrice = usePriceSquireBusd()

  // Add Farms
  let value = new BigNumber(0)
  for (let i = 0; i < farms.length; i++) {
    const farm = farms[i]
    if (farm.lpTotalInQuoteToken) {
      let val
      if (farm.quoteTokenSymbol === QuoteToken.BNB) {
        val = bnbPrice.times(farm.lpTotalInQuoteToken)
      } else if (farm.quoteTokenSymbol === QuoteToken.CAKE) {
        val = cakePrice.times(farm.lpTotalInQuoteToken)
      } else if (farm.quoteTokenSymbol === QuoteToken.SQUIRE) {
        val = squirePrice.times(farm.lpTotalInQuoteToken)
      } else if (farm.quoteTokenSymbol === QuoteToken.ETH) {
        val = ethPrice.times(farm.lpTotalInQuoteToken)
      } else {
        val = farm.lpTotalInQuoteToken
      }
      value = value.plus(val)
    }
  }
  
  for (let i = 0; i < battlefield.length; i++) {
    const bf = battlefield[i]
    if (bf.quoteTokenAmount) {
      const bfStaking = new BigNumber(bf.quoteTokenAmount).toNumber() - new BigNumber(bf.rewardsBalance).toNumber()
      if (bf.tokenSymbol === QuoteToken.KNIGHT){
        value = value.plus(cakePrice.times(bfStaking))
      }
      if (bf.tokenSymbol === QuoteToken.LEGEND){
        value = value.plus(legendPrice.times(bfStaking))
      }
      if (bf.tokenSymbol === QuoteToken.TABLE){
        value = value.plus(tablePrice.times(bfStaking))
      }
      if (bf.tokenSymbol === QuoteToken.SQUIRE){
        value = value.plus(squirePrice.times(bfStaking))
      }
    }
  }

  // Add pool as well! -- Sir Tris
  const val = cakePrice.times(pool.totalStaked).div(1e18)
  value = value.plus(val)

  return value
}

export const useTotalRewards = (): BigNumber => {
  const farms = useFarms()
  const battlefield = useBattlefield()
  const pool = usePoolFromPid(0)
  const cakePrice = usePriceCakeBusd()
  const legendPrice = usePriceLegendBusd()
  const tablePrice = usePriceTableBusd()
  const squirePrice = usePriceSquireBusd()

  // Add Farms
  let value = new BigNumber(0)
  
  for (let i = 0; i < battlefield.length; i++) {
    const bf = battlefield[i]
    if (bf.rewardsBalance) {
      if (bf.tokenSymbol === QuoteToken.KNIGHT){
        value = value.plus(cakePrice.times(bf.rewardsBalance))
      }
      if (bf.tokenSymbol === QuoteToken.LEGEND){
        value = value.plus(legendPrice.times(bf.rewardsBalance))
      }
      if (bf.tokenSymbol === QuoteToken.TABLE){
        value = value.plus(tablePrice.times(bf.rewardsBalance))
      }
      if (bf.tokenSymbol === QuoteToken.SQUIRE){
        value = value.plus(squirePrice.times(bf.rewardsBalance))
      }
    }
  }

  // Add pool as well! -- Sir Tris
  const val = cakePrice.times(pool.totalStaked).div(1e18)
  value = value.plus(val)

  return value
}

